/**
 * SG — AGM with Audited Financial Statements (7 documents). Task code: B32204.
 */
import { SG_AGM_AFS_TASK_CODE } from '../../../../constants/sgAgmTaskCodes';
import { dedupePresetSigners } from '../../../presetSignerDedup';

export interface MemberRow {
  name: string;
  address: string;
  contactCd?: string;
}

export interface RetiringDirectorRow {
  name: string;
}

export interface SignerRow {
  name: string;
}

export interface AgmEntityPersonRow {
  name: string;
  contactRegNo?: string;
  contactCd?: string;
  email?: string;
  role?: 'director' | 'shareholder';
}

export interface AgmAfsFormModel {
  companyName: string;
  companyRegistrationNo: string;
  registeredOfficeAddress: string;

  agmVenue: string;
  agmDate: string;
  agmTime: string;

  financialYearEndDate: string;
  annualReturnYear: string;

  isShortNotice: boolean;
  hasDirectorsRemuneration: boolean;
  hasRetiringDirectors: boolean;
  auditorName: string;

  memberType: 'multiple' | 'sole_corporate' | 'sole_individual';
  members: MemberRow[];
  memberName: string;
  memberRepresentativeName: string;
  chairmanName: string;

  directorCount: number;
  specifySigners: boolean;
  signers: SignerRow[];
  retiringDirectors: RetiringDirectorRow[];

  entityDirectors: AgmEntityPersonRow[];
  entityMembers: AgmEntityPersonRow[];

  noticeDate: string;
  consentDate: string;
  resolutionDate: string;
}

export function createEmptyAgmAfsForm(): AgmAfsFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    registeredOfficeAddress: '',
    agmVenue: '',
    agmDate: '',
    agmTime: '',
    financialYearEndDate: '',
    annualReturnYear: '',
    isShortNotice: false,
    hasDirectorsRemuneration: false,
    hasRetiringDirectors: false,
    auditorName: '',
    memberType: 'multiple',
    members: [],
    memberName: '',
    memberRepresentativeName: '',
    chairmanName: '',
    directorCount: 1,
    specifySigners: false,
    signers: [],
    retiringDirectors: [],
    entityDirectors: [],
    entityMembers: [],
    noticeDate: '',
    consentDate: '',
    resolutionDate: '',
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** FYE 기준 AR 연도 힌트 (비어 있으면 FYE+1년) */
export function deriveAnnualReturnYearFromFye(fye: string): string {
  const s = trim(fye);
  if (!s) return '';
  const m = s.match(/^(\d{4})/);
  if (m) return String(Number(m[1]) + 1);
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return String(d.getFullYear() + 1);
  return '';
}

export function toAgmAfsTemplateVariables(m: AgmAfsFormModel): Record<string, unknown> {
  const isMultiple = m.memberType === 'multiple';
  const isSoleCorporate = m.memberType === 'sole_corporate';
  const isSoleIndividual = m.memberType === 'sole_individual';

  const members = (m.members || []).map((r) => ({
    member_name: trim(r.name),
    member_address: trim(r.address),
  }));

  const annualReturnYear = trim(m.annualReturnYear) || deriveAnnualReturnYearFromFye(m.financialYearEndDate);

  return {
    company_name: trim(m.companyName),
    company_registration_no: trim(m.companyRegistrationNo),
    registered_office_address: trim(m.registeredOfficeAddress),
    agm_venue: trim(m.agmVenue),
    agm_date: trim(m.agmDate),
    agm_time: trim(m.agmTime),
    financial_year_end_date: trim(m.financialYearEndDate),
    annual_return_year: annualReturnYear,
    is_short_notice: m.isShortNotice,
    has_directors_remuneration: m.hasDirectorsRemuneration,
    has_retiring_directors: m.hasRetiringDirectors,
    auditor_name: trim(m.auditorName),
    is_multiple_members: isMultiple,
    is_sole_corporate_member: isSoleCorporate,
    is_sole_individual_member: isSoleIndividual,
    members,
    member_name: isSoleCorporate || isSoleIndividual ? trim(m.memberName) : '',
    member_representative_name: isSoleCorporate ? trim(m.memberRepresentativeName) : '',
    chairman_name: trim(m.chairmanName),
    director_count: m.directorCount,
    'director_count == 1': m.directorCount === 1,
    'director_count == 2': m.directorCount === 2,
    specify_signers: m.specifySigners,
    notice_date: trim(m.noticeDate),
    consent_date: trim(m.consentDate),
    resolution_date: trim(m.resolutionDate),
    agm_afs: true,
  };
}

export function toAgmAfsRetiringDirectorsRepeatingData(
  m: AgmAfsFormModel,
): Array<Record<string, string>> {
  return (m.retiringDirectors || [])
    .map((r) => {
      const name = trim(r.name);
      return { 'director.name': name, director_name: name };
    })
    .filter((row) => row['director.name']);
}

export function toAgmAfsDirectorsResolutionSignerVariables(m: AgmAfsFormModel): Record<string, unknown> {
  const signers_list = (m.signers || []).map((s) => ({
    signer_name: trim(s.name),
  }));
  const deduped = dedupePresetSigners(
    signers_list
      .map((s) => ({
        name: trim(s.signer_name),
        email: '',
        role: 'director',
      }))
      .filter((s) => s.name),
    { chairmanName: trim(m.chairmanName) },
  );
  const signersJson = deduped.length ? JSON.stringify(deduped) : '';
  return {
    designated_signers: signers_list,
    signers_list,
    signers: signersJson,
    Signers: signersJson,
  };
}

export function toAgmAfsDirectorsResolutionOnlyVariables(m: AgmAfsFormModel): Record<string, unknown> {
  return {
    ...toAgmAfsDirectorsResolutionSignerVariables(m),
    retiring_directors: toAgmAfsRetiringDirectorsRepeatingData(m),
  };
}

export function buildAgmAfsTaskFormData(m: AgmAfsFormModel, _entityCd: string): Record<string, unknown> {
  return {
    companyName: trim(m.companyName),
    companyRegistrationNo: trim(m.companyRegistrationNo),
    registeredOfficeAddress: trim(m.registeredOfficeAddress),
    agmVenue: trim(m.agmVenue),
    agmDate: trim(m.agmDate),
    agmTime: trim(m.agmTime),
    financialYearEndDate: trim(m.financialYearEndDate),
    annualReturnYear: trim(m.annualReturnYear),
    memberType: m.memberType,
    memberName: trim(m.memberName),
    memberRepresentativeName: trim(m.memberRepresentativeName),
    chairmanName: trim(m.chairmanName),
    isShortNotice: m.isShortNotice,
    hasDirectorsRemuneration: m.hasDirectorsRemuneration,
    hasRetiringDirectors: m.hasRetiringDirectors,
    auditorName: trim(m.auditorName),
    directorCount: m.directorCount,
    specifySigners: m.specifySigners,
    signers: (m.signers || []).map((s) => ({ name: trim(s.name) })),
    retiringDirectors: (m.retiringDirectors || []).map((r) => ({ name: trim(r.name) })),
    members: (m.members || []).map((r) => ({
      name: trim(r.name),
      address: trim(r.address),
      contactCd: trim(r.contactCd),
    })),
    noticeDate: trim(m.noticeDate),
    consentDate: trim(m.consentDate),
    resolutionDate: trim(m.resolutionDate),
    agm_afs: true,
    taskTypeCd: SG_AGM_AFS_TASK_CODE,
  };
}

export function isAgmAfsFormComplete(m: AgmAfsFormModel): boolean {
  const base = [
    m.companyName,
    m.companyRegistrationNo,
    m.agmVenue,
    m.agmDate,
    m.agmTime,
    m.financialYearEndDate,
    m.noticeDate,
    m.auditorName,
  ].every((v) => trim(v).length > 0);

  if (!base) return false;

  if (m.memberType === 'multiple') {
    if (m.members.length < 2) return false;
    if (!m.members.every((r) => trim(r.name))) return false;
    if (!trim(m.chairmanName)) return false;
  } else if (m.memberType === 'sole_corporate') {
    if (!trim(m.memberName) || !trim(m.memberRepresentativeName)) return false;
  } else if (m.memberType === 'sole_individual') {
    if (!trim(m.memberName)) return false;
  }

  if (m.hasRetiringDirectors) {
    if (m.retiringDirectors.length === 0) return false;
    if (!m.retiringDirectors.every((r) => trim(r.name))) return false;
  }
  if (m.specifySigners && !m.signers.some((s) => trim(s.name))) return false;
  if (m.isShortNotice && !trim(m.consentDate)) return false;
  if (!trim(m.resolutionDate)) return false;

  return true;
}
